import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHeaderConstants } from '@r1-compass-library/constants';
import { catchError, finalize, Observable, of, retry } from 'rxjs';
import { SpinnerService } from './spinner.service';
import { QueryParamsConstants } from '@r1-compass-library/constants/query-params.constants';
import { Environment } from '@r1-compass-library/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class FileTransferService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private DOWNLOAD_URL: string = Environment.baseUrl + '/frontend/s3/download';
    private PRESIGNEDURL_URL: string = Environment.baseUrl + '/frontend/s3/url';
    private FILEPREVIEW_URL: string = Environment.baseUrl + '/frontend/s3/preview';
    private FILE_VALIDATION_URL: string = Environment.baseUrl + '/frontend/s3/validate';

    getSignedUrl(
        fileName: string,
        assetId: string,
        assetType: 'play' | 'content' = 'play',
        httpMethod: 'PUT' | 'GET' = 'PUT'
    ): Observable<S3PresignedUrl> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(QueryParamsConstants.FILE_NAME, fileName)
                .set(QueryParamsConstants.ASSET_ID, assetId)
                .set(QueryParamsConstants.ASSET_TYPE, assetType)
                .set(QueryParamsConstants.HTTP_METHOD, httpMethod),
        };
        const process = 'FileTransferService.getSignedUrl';
        this.#spinnerService.addProcess(process, 'Getting upload URL...');
        return this.#http.get<S3PresignedUrl>(this.PRESIGNEDURL_URL, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<S3PresignedUrl>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getFile(fileName: string, playId: string): Observable<Blob> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.fileDownload }),
            params: new HttpParams()
                .set(QueryParamsConstants.FILE_NAME, fileName)
                .set(QueryParamsConstants.PLAY_ID, playId),
            responseType: 'blob' as const,
        };

        const process = 'FileTransferService.getFile';
        this.#spinnerService.addProcess(process, 'Retrieving file...');

        return this.#http.get(this.DOWNLOAD_URL, httpOptions).pipe(
            retry(0),
            catchError(_error => {
                return of<Blob>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    getFilePreview(fileName: string, playId: string): Observable<Array<string[]>> {
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: new HttpParams()
                .set(QueryParamsConstants.FILE_NAME, fileName)
                .set(QueryParamsConstants.PLAY_ID, playId),
        };
        const process = 'FileTransferService.getFilePreview';
        this.#spinnerService.addProcess(process, 'Getting file preview...');
        return this.#http.get<Array<string[]>>(this.FILEPREVIEW_URL, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<Array<string[]>>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    validateFile(fileName: string, playId: string, uniqueColumnName?: string): Observable<CsvValidationResponse> {
        let params = new HttpParams()
            .set(QueryParamsConstants.FILE_NAME, fileName)
            .set(QueryParamsConstants.PLAY_ID, playId);
        if (uniqueColumnName) {
            params = params.set(QueryParamsConstants.UNIQUE_COLUMN_NAME, uniqueColumnName);
        }

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders }),
            params: params,
        };
        const process = 'FileTransferService.validateFile';
        this.#spinnerService.addProcess(process, 'Validating file...');
        return this.#http.get<CsvValidationResponse>(this.FILE_VALIDATION_URL, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<CsvValidationResponse>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }

    uploadFile(file: File, presignedUrl: string, contentType: string): Observable<string> {
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                'Content-Type': contentType,
            }),
        };
        const process = 'FileTransferService.uploadFile';
        this.#spinnerService.addProcess(process, 'Uploading file to S3...');
        return this.#http.put<string>(presignedUrl, file, httpOptions).pipe(
            retry(0),
            catchError(error => {
                return of<string>();
            }),
            finalize(() => this.#spinnerService.completeProcess(process))
        );
    }
}

export interface S3PresignedUrl {
    url: string;
    contentType: string;
}

export type CsvValidationResponse = {
    valid: boolean;
    errorMessage?: string;
};
